import { useState, useEffect } from "react";
import SaveImageUseCase from "../../usecases/file/SaveImageUseCase";
import BaseListPresenter from "../../base/BaseListPresenter";
import { deleteObjectUseCase, upsertUseCase } from "../../usecases/object";
import SaveFileUseCase from "../../usecases/file/SaveFileUseCase";
import { Button, dialog } from "nq-component";
import Queue from "nq";

export function convertTo12Hour(time) {
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const adjustedHour = hour % 12 || 12;
  return `${adjustedHour}:${minutes} ${ampm}`;
}

export function convertTo24HourTime(time) {
  const [timePart, modifier] = time?.split(" ");
  let [hours, minutes] = timePart.split(":").map(Number);

  if (modifier === "PM" && hours !== 12) {
    hours += 12;
  } else if (modifier === "AM" && hours === 12) {
    hours = 0;
  }
  return { hours: hours || 0, minutes: minutes || 0 };
}

export function formatDate(dateStr) {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    return "-";
  }

  const options = { month: "long", day: "numeric", year: "numeric" };
  return date.toLocaleDateString("en-US", options);
}

export function formatTodayKey() {
  return new Date()
    .toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
    })
    .replace(/\//g, "-");
}

export function parseTime(timeString) {
  const [time, modifier] = timeString.split(" ");
  let [hours, minutes] = time.split(":").map(Number);

  if (modifier === "PM" && hours < 12) {
    hours += 12;
  } else if (modifier === "AM" && hours === 12) {
    hours = 0; // Adjust for midnight
  }

  const date = new Date();

  date.setHours(hours, minutes, 0, 0);
  return date;
}

export function getMonthIndex(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const sameMonth =
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear();
  if (!sameMonth) return 0;

  const month = start.getMonth();

  return 12 - month;
}

export function CreditsDeduction(
  hasLeaveCred,
  vacationAddValue,
  sickAddValue,
  deductionValue,
) {
  // Increment Vacation Leave and Sick Leave separately by their respective add values, fixed to 3 decimals
  hasLeaveCred.forEach((item) => {
    if (item.type === "Sick Leave") {
      item.current = parseFloat(item.current) + parseFloat(sickAddValue);
    } else if (item.type === "Vacation Leave") {
      item.current = parseFloat(item.current) + parseFloat(vacationAddValue);
    }
  });

  let remainingDeduction = deductionValue;
  let vacationLeaveDeduction = "0"; // To track how much is deducted from Vacation Leave

  // Find Sick Leave first and apply the deduction
  const sickLeave = hasLeaveCred.find((item) => item.type === "Sick Leave");
  if (sickLeave) {
    if (sickLeave.current >= remainingDeduction) {
      // Deduct entirely from Sick Leave if enough balance
      sickLeave.current = parseFloat(
        (sickLeave.current - remainingDeduction).toFixed(3),
      );
      remainingDeduction = 0;
    } else {
      // Deduct what’s available and update remaining deduction
      remainingDeduction -= sickLeave.current;
      sickLeave.current = 0;
    }
  }

  // If there's remaining deduction, apply to Vacation Leave
  if (remainingDeduction > 0) {
    const vacationLeave = hasLeaveCred.find(
      (item) => item.type === "Vacation Leave",
    );
    if (vacationLeave) {
      let initialVacationBalance = vacationLeave.current; // Store initial balance
      vacationLeave.current = parseFloat(
        vacationLeave.current - remainingDeduction,
      );
      vacationLeaveDeduction = (
        initialVacationBalance - vacationLeave.current
      ).toFixed(3); // Calculate how much was deducted
    }
  }
  // Convert all leave balances to string with 3 decimal places
  hasLeaveCred.forEach((item) => {
    item.current = parseFloat(item.current).toFixed(3);
  });

  // Return the updated leave credits data along with vacation leave deduction info
  return {
    updatedCredits: hasLeaveCred,
    vacationLeaveDeduction: vacationLeaveDeduction,
  };
}

export function parseTimeLogs(timeString) {
  const [time, modifier] = timeString.split(" ");
  let [hours, minutes, seconds] = time.split(":").map(Number);

  if (modifier === "PM" && hours < 12) {
    hours += 12;
  } else if (modifier === "AM" && hours === 12) {
    hours = 0; // Adjust for midnight
  }

  const date = new Date();

  date.setHours(hours, minutes, seconds, 0);
  return date;
}

export function formatTime(dateNow) {
  const date = dateNow;

  // Extract hours, minutes, and seconds
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  // Determine AM or PM
  const ampm = hours >= 12 ? "PM" : "AM";

  // Convert hours to 12-hour format
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'

  // Format minutes and seconds to always be two digits
  const formattedMinutes = minutes < 10 ? "0" + minutes : minutes;
  const formattedSeconds = seconds < 10 ? "0" + seconds : seconds;

  const formattedTime = `${hours}:${formattedMinutes}:${formattedSeconds} ${ampm}`;

  return formattedTime;
}

export function FloatingForm({ children, width }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);

    document.body.classList.add("modal-open");

    setIsVisible(true);

    return () => {
      document.body.classList.remove("modal-open");
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div
      className={`w-100 h-100 position-fixed end-0 start-0 top-0 overflow-y-auto d-flex justify-content-center align-items-center ${
        isVisible ? "show" : ""
      }`}
      style={{
        zIndex: 1050,
        backgroundColor: "rgba(0, 0, 0, 0.4)",
        opacity: isVisible ? 1 : 0,
        transition: "opacity 0.3s ease",
      }}
    >
      <div
        className="w-100 d-flex column align-items-center"
        style={{
          maxHeight: "99%",
          flexDirection: "column",
          transform: isVisible ? "scale(1)" : "scale(0.8)",
          transition: "transform 0.3s ease, opacity 0.3s ease",
        }}
      >
        <div
          style={{
            width: isMobile ? "90%" : width,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export async function SubmitImg(ArrayFiles) {
  // export async function SubmitFiles(ArrayFiles) {
  return await Promise.all(
    ArrayFiles?.map(async (file) => {
      if (typeof file === "string") {
        return file; // Already a URL, no need to process further
      } else if (file.type.startsWith("image/")) {
        // Handle image files
        const options = {
          maxWidth: 300,
          maxHeight: 300,
        };
        const blob = new Blob([file]);
        blob.name = file.name;
        const result = await new SaveImageUseCase().execute(blob, options);
        console.warn("Img: ", result);
        return result.url;
      } else {
        // Handle non-image files (e.g., PDF)
        try {
          const blob = new Blob([file], { type: file.type });
          blob.name = file.name;
          const result = await new SaveFileUseCase().execute(blob);
          return result.url;
        } catch (error) {
          console.error("Failed to process file:", error);
          throw error;
        }
      }
    }),
  );
}

export class SubmitFormBaseList extends BaseListPresenter {
  async Save(collection, object) {
    try {
      console.warn("OBJE: ", object);
      console.warn("Coole: ", collection);
      await upsertUseCase().execute(collection, object);
      // this.showSuccess(message);
    } catch (error) {
      console.error("Presenter: ", error);
      // this.showError(error.message);
      throw new Error(error.message);
    }
  }
}

// export class currentUser extends MainPagePresenter {
//   render() {
//     return this.getC();
//   }
// }

export function numberToWords(num) {
  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  if (num === 0) return "Zero";

  let words = "";

  if (Math.floor(num / 1_000_000_000) > 0) {
    words += numberToWords(Math.floor(num / 1_000_000_000)) + " Billion ";
    num %= 1_000_000_000;
  }

  if (Math.floor(num / 1_000_000) > 0) {
    words += numberToWords(Math.floor(num / 1_000_000)) + " Million ";
    num %= 1_000_000;
  }

  if (Math.floor(num / 1000) > 0) {
    words += numberToWords(Math.floor(num / 1000)) + " Thousand ";
    num %= 1000;
  }

  if (Math.floor(num / 100) > 0) {
    words += numberToWords(Math.floor(num / 100)) + " Hundred ";
    num %= 100;
  }

  if (num > 0) {
    if (num < 20) {
      words += ones[num];
    } else {
      words += tens[Math.floor(num / 10)];
      if (num % 10 > 0) words += "-" + ones[num % 10];
    }
  }

  return words.trim();
}

export function formatNumberToWords(amount) {
  const isNegative = amount < 0;
  const absoluteAmount = Math.abs(amount);
  const integerPart = Math.floor(absoluteAmount);
  const decimalPart = Math.round((absoluteAmount - integerPart) * 100);

  let words = numberToWords(integerPart);
  if (decimalPart > 0) {
    const decimalWords = numberToWords(decimalPart);
    words += ` Pesos & ${decimalWords} Centavos Only`;
  } else {
    words += ` Pesos Only`;
  }

  return isNegative ? `Negative ${words}` : words;
}

export function generateUniqueId(length = 20) {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let id = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * chars.length);
    id += chars.charAt(randomIndex);
  }
  return id;
}

// export async function processSessionData(
//   sessionKey,
//   deletedKey,
//   collection,
//   objectID
// ) {
//   const sessionData = JSON.parse(localStorage.getItem(sessionKey)) || [];
//   const deletedData = JSON.parse(localStorage.getItem(deletedKey)) || [];
//   // Data Restoration!
//   async function DeletionMethods(sessionData, collection) {
//     const collectionData = await findObjectUseCase().execute(collection);
//     for (const item of sessionData) {
//       if (collectionData.some((obj) => obj.id === item.id)) {
//         await deleteObjectUseCase().execute(collection, item.id);
//       }
//     }
//   }

//   async function RestoreMethods(sessionData, collection) {
//     for (const item of sessionData) {
//       await upsertUseCase().execute(collection, item);
//     }
//   }

//   if (sessionData.length > 0) {
//     await DeletionMethods(sessionData, collection);
//   } else if (deletedData.length > 0 && objectID !== undefined) {
//     await RestoreMethods(deletedData, collection, objectID);
//   }
// }

export const parseDateToISO = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr + " UTC");
  return date.toISOString().split("T")[0];
};

export function formattedRate(rate) {
  return parseFloat(rate.replace(/[^0-9.-]+/g, "")).toFixed(2);
}

export function normalizeDate(date) {
  let parsedDate;
  if (typeof date === "string" && date.includes(" at")) {
    parsedDate = new Date(date.replace(" at", ""));
  } else if (date instanceof Date) {
    parsedDate = date;
  } else {
    console.log("Please Select a Vlid Date");
  }
  return new Date(parsedDate.setHours(0, 0, 0, 0));
}

export function formatDateRange(start, end) {
  const [s, e] = [new Date(start), new Date(end)];
  return `${s.toLocaleString("en-US", {
    month: "short",
  })} ${s.getDate()}-${e.getDate()}, ${s.getFullYear()}`;
}

export const getDatesInRange = (start, end) => {
  const dates = [];
  let current = new Date(start);
  while (current <= end) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  const daysOfWeek = dates.map((date) =>
    date.toLocaleString("en-us", { weekday: "long" }).toLowerCase(),
  );

  return daysOfWeek;
};

export const getLastId = (obj) => {
  if (!obj) return "";

  while (obj?.id) {
    obj = obj.id;
  }
  return obj ?? "";
};

export const formatDateObj = (date, label) => {
  return date
    ? date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "Not Selected";
};

export function extractSalary(salaryString) {
  if (typeof salaryString !== "string") return null;

  const match = salaryString.match(/₱\s*([\d\s,.]+)|(\d+(\.\d+)?)(?=\s*Hours?)|^\s*(\d+(\.\d+)?)\s*$/);

  if (!match) return null;

  if (match[1]) {
    return parseFloat(
      match[1].replace(/[\s,]/g, "")
    );
  }

  if (match[2]) {
    return parseFloat(match[2]);
  }

  if (match[4]) {
    return parseFloat(match[4]);
  }

  return null;
}


export const formattedDate = (startDate, endDate) => {
  const [start, end] = [new Date(startDate), new Date(endDate)];

  return start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear()
    ? `${start.toLocaleString("default", {
        month: "long",
      })} ${start.getDate()} - ${end.getDate()}, ${start.getFullYear()}`
    : `${start.toLocaleString("default", {
        month: "long",
      })} ${start.getDate()}, ${start.getFullYear()} - ${end.toLocaleString(
        "default",
        { month: "long" },
      )} ${end.getDate()}, ${end.getFullYear()}`;
};

export const formatNumber = (inputValue) => {
  if (inputValue == null || inputValue === "") return "";

  let value = String(inputValue);

  // Remove all characters except digits and dots
  value = value.replace(/[^0-9.]/g, "");

  // Keep only the first dot if there are multiple
  const parts = value.split(".");
  const integerPart = parts[0];
  const decimalPart = parts.length > 1 ? parts.slice(1).join("") : "";

  const formattedIntegerPart = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ",",
  );

  // Preserve trailing dot while typing
  if (value.endsWith(".") && parts.length === 2) {
    return `${formattedIntegerPart}.`;
  }

  return decimalPart
    ? `${formattedIntegerPart}.${decimalPart}`
    : `${formattedIntegerPart}`;
};

export function formatCurrency(rate) {
  const numValue = parseFloat(rate.replace(/[^0-9.-]+/g, ""));

  if (!isNaN(numValue)) {
    const formattedValue = numValue.toFixed(2);
    const res = Number(formattedValue).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return res;
  }
  return "-";
}

export function insertAfterKey(obj, keyToInsertAfter, newKey, newValue) {
  const result = {};
  for (const key in obj) {
    result[key] = obj[key];
    if (key === keyToInsertAfter) {
      result[newKey] = newValue;
    }
  }
  return result;
}

export const sumArray = (arr) => {
  if (!Array.isArray(arr)) return 0;
  return arr.reduce((total, num) => total + num, 0);
};

export function DialogConfirmation(id, collection, refetch, permanent) {
  dialog.fire({
    html: (
      <div className="p-2">
        <div className="row p-1">
          <h4>Delete Confirmation</h4>
          <hr />
          <p style={{ fontSize: "13px", padding: "1rem" }}>
            Are you sure you want to delete?
          </p>
          <hr />
        </div>
        <div className="w-100 d-flex justify-content-end gap-2 pe-3 pb-3">
          <Button
            className="btn btn-light fs-sm border-primary"
            onClick={() => dialog.close()}
          >
            Cancel
          </Button>
          <Button
            className="btn btn-primary"
            onClick={async () => {
              if (permanent) {
                await new Queue.Document().delete(collection, id);
                dialog.close();
                refetch();
              } else {
                await deleteObjectUseCase().execute(collection, id);
                dialog.close();
                refetch();
              }
            }}
          >
            Confirm
          </Button>
        </div>
      </div>
    ),
    footer: false,
  });
}

export function Instructions() {
  dialog.fire({
    html: (
      <div className="p-2">
        <div className="row " style={{ textAlign: "center" }}>
          <p>
            {/* <i
              class="bi bi-question-circle"
              style={{ color: "orange", fontSize: "20px" }}
            ></i>{" "} */}
            🔹 Features
          </p>
          <p
            style={{
              fontSize: "13px",
              color: "rgba(0, 0, 0, .65)",
            }}
          >
            ✔ After input click Enter → Adds data to the list.
            <br /> ✔ Click on an item from the list → Edits the item.
            <br /> ✔ Click the ❌ button → Deletes the item.
          </p>
          <hr />
        </div>

        <div className="w-100 d-flex justify-content-end gap-3 pe-3 pb-3">
          <Button
            className="btn btn-light fs-sm"
            onClick={() => dialog.close()}
          >
            Ok
          </Button>
        </div>
      </div>
    ),
    footer: false,
  });
}

export function minuteConverterToCredit(undertimeMinutes) {
  let conversionValue = 0.0;

  for (let i = 1; i <= undertimeMinutes; i++) {
    // Add 0.003 at 7th, 19th, 31st, 43rd, 55th minute of every hour
    if (
      i % 60 === 7 ||
      i % 60 === 19 ||
      i % 60 === 31 ||
      i % 60 === 43 ||
      i % 60 === 55
    ) {
      conversionValue += 0.003;
    } else {
      conversionValue += 0.002;
    }
  }

  return conversionValue.toFixed(3).toString();
}

export function ServiceRecordLeave(abbreviation, startDate, endDate) {
  if (!startDate || !endDate) return "";

  const startDateObj = new Date(startDate);
  const endDateObj = new Date(endDate);

  const startMonth = String(startDateObj.getMonth() + 1).padStart(2, "0");
  const startDay = String(startDateObj.getDate()).padStart(2, "0");
  const startYear = startDateObj.getFullYear();

  const endMonth = String(endDateObj.getMonth() + 1).padStart(2, "0");
  const endDay = String(endDateObj.getDate()).padStart(2, "0");
  const endYear = endDateObj.getFullYear();

  let formattedLeave = "";

  if (startYear === endYear && startMonth === endMonth) {
    // Same year and month
    formattedLeave = `${abbreviation} ${startMonth}/${startDay}-${endDay}/${startYear}`;
  } else {
    // Different month or year
    formattedLeave = `${abbreviation} ${startMonth}/${startDay}/${startYear}-${endMonth}/${endDay}/${endYear}`;
  }

  return formattedLeave;
}

let cachedTime = null;
let cachedAt = 0;

export async function GetCurrentManilaTimeAPICall() {
  const now = Date.now();

  // Return cached time if still valid
  if (cachedTime && now - cachedAt < 5 * 60 * 1000) {
    return new Date(cachedTime + (now - cachedAt));
  }

  try {
    // Use WorldTimeAPI - generally faster and more reliable
    const res = await fetch(
      "https://timezone.innque.com/api/timezone/Asia/Manila",
      { cache: "no-store" },
    );

    if (!res.ok) throw new Error("API failed");

    const data = await res.json();
    console.log("apiCall: ", data);
    cachedTime = new Date(data.datetime).getTime();
    cachedAt = now;

    return new Date(cachedTime);
  } catch {
    // Fallback: calculate Manila time from local time
    const local = new Date();
    const utc = local.getTime() + local.getTimezoneOffset() * 60000;
    return new Date(utc + 8 * 3600000); // Manila is UTC+8
  }
}

export default function FormTable({ title, child }) {
  return (
    <div className="row me-2">
      {/* <div className="col-lg-6 border  p-0"> */}
      <div
        // style={{ backgroundColor: "#c1c1c1" }}
        className="p-1 me"
      >
        <label className="fs-sm">{title}</label>
      </div>
      <div className="row p-0 m-0">{child}</div>
    </div>
    // </div>
  );
}
